import { useState } from "react";
import { useGetNicknameQuery, useSetNicknameMutation } from "@/api/api";
import { getApiErrorDetail } from "@/api/errors";
import { useNicknameCheck } from "@/api/hooks";
import { MIN_NICKNAME_LENGTH } from "@/common/slug";
import { Button } from "../button";
import UrlNameField from "./UrlNameField";

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export default function NicknameInput() {
  const { data: status } = useGetNicknameQuery();
  const [setNickname, { isLoading }] = useSetNicknameMutation();
  const [value, setValue] = useState("");
  const [valueForCurrent, setValueForCurrent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const current = status?.nickname ?? null;

  if (current !== valueForCurrent) {
    setValueForCurrent(current);
    setValue(current || "");
    setError(null);
  }

  const check = useNicknameCheck(value, current);

  // Server-side limits. Locking the field is friendlier than letting someone
  // craft a name and only then being told they cannot have it.
  const cooldownUntil = status?.change_available_at || null;
  const claimsLeft = status?.claims_left ?? 0;
  const outOfClaims = !current ? false : claimsLeft <= 0;
  const locked = !!cooldownUntil || outOfClaims;

  const changed = check.normalized !== (current || "");
  const canSave = changed && check.state === "ok" && !locked && !isLoading;

  const handleSave = async () => {
    setError(null);
    try {
      await setNickname(check.normalized).unwrap();
    } catch (e) {
      setError(
        getApiErrorDetail(e, "Could not set that nickname. Please try again."),
      );
    }
  };

  let hint: string;
  if (cooldownUntil) {
    hint = `You changed your nickname recently. You can change it again after ${formatDate(cooldownUntil)}.`;
  } else if (outOfClaims) {
    hint = "You have used all of your nickname changes.";
  } else if (!current) {
    hint = `At least ${MIN_NICKNAME_LENGTH} characters, latin letters a-z, digits and hyphens. Until you pick one, your links use your account id.`;
  } else {
    // Worth saying plainly: this is the part people get wrong about renaming.
    hint = `Your old nickname keeps redirecting here and can never be taken by anyone else. ${claimsLeft} change${claimsLeft === 1 ? "" : "s"} left.`;
  }

  return (
    <div className="flex flex-col gap-2">
      <UrlNameField
        id="nickname"
        label="Nickname"
        value={value}
        onChange={setValue}
        check={error ? { ...check, state: "error", message: error } : check}
        prefix={`${window.location.host}/`}
        hint={hint}
        placeholder="your-name"
        disabled={locked}
      />
      <div className="flex justify-end">
        <Button
          type="button"
          variant="outline"
          disabled={!canSave}
          onClick={handleSave}
        >
          Save
        </Button>
      </div>
    </div>
  );
}
